import React, { useEffect, useRef, useState } from "react";
import "../styles/login.scss";
import CustomButton from "../components/reusable/CustomButton";
import { useNotification } from "../context/notificationContext";
import { X } from "lucide-react";
import styled from "styled-components";
import { logoMob } from "../assests/imageModule/image";
import { useAuth } from "../context/userContext";

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  transition: color 0.2s ease;
  width: 30px;
  height: 30px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  z-index: 999;
  right: 0;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 20px;

  transition: 0.4s;

  &:hover {
    background: red;
  }
`;

const Login = ({ show, setShow }) => {
  const [, , showNotification] = useNotification();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);
  const { refetchProfile } = useAuth();

  useEffect(() => {
    // focus first OTP box when OTP sent
    if (otpSent) {
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    }
  }, [otpSent]);

  const handleSendOtp = async () => {
    if (mobile.trim().length === 10) {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_LIVE}/request-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              mobileNo: mobile,
            }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setOtp(new Array(6).fill(""));
          setOtpSent(true);
          showNotification("success", "OTP sent successfully");
        } else {
          showNotification("error", data.message || "Failed to send OTP");
        }
      } catch (error) {
        console.error("Error sending OTP:", error);
        showNotification("error", "Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      showNotification("error", "Please enter a valid 10 digit mobile number");
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_LIVE}/resend-otp/${mobile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setOtp(new Array(6).fill(""));
        showNotification("success", "OTP resent successfully");
        setTimeout(() => inputsRef.current[0]?.focus(), 50);
      } else {
        showNotification("error", data.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      showNotification("error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    const nextOtp = [...otp];

    if (!val) {
      nextOtp[idx] = "";
      setOtp(nextOtp);
      return;
    }

    if (val.length === 1) {
      nextOtp[idx] = val;
      setOtp(nextOtp);
      if (idx < 5) inputsRef.current[idx + 1]?.focus();
    } else {
      const digits = val.split("");
      for (let i = 0; i < digits.length && idx + i < 6; i++) {
        nextOtp[idx + i] = digits[i];
      }
      setOtp(nextOtp);
      const focusIndex = Math.min(5, idx + digits.length);
      inputsRef.current[focusIndex]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const nextOtp = [...otp];

      if (otp[idx]) {
        nextOtp[idx] = "";
        setOtp(nextOtp);
        inputsRef.current[idx]?.focus();
      } else if (idx > 0) {
        nextOtp[idx - 1] = "";
        setOtp(nextOtp);
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault();
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("Text").replace(/\D/g, "");
    if (!paste) return;
    const nextOtp = [...otp];
    for (let i = 0; i < paste.length && idx + i < 6; i++) {
      nextOtp[idx + i] = paste[i];
    }
    setOtp(nextOtp);
    const focusIndex = Math.min(5, idx + paste.length);
    inputsRef.current[focusIndex]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      showNotification("error", "Please enter the full 6 digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_LIVE}/verify-otp/${mobile}/${code}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      const data = await response.json();
      if (response?.ok) {
        await refetchProfile();
      } else {
        showNotification("error", data.message || "Invalid OTP");
      }
    } catch (error) {
      showNotification("error", "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`login_wrapper ${show && "show"}`}>
      <div className="login_form">
        <CloseButton
          title="Close"
          onClick={() => {
            setShow(false);
            setOtpSent(false);
          }}
        >
          <X size={20} color="#fff" />
        </CloseButton>
        <div>
          <img className="logo" src={logoMob} alt="saaj_logo" />
          <h1 className="login_title">
            Login to <span>Saaj Farms</span>
          </h1>

          {!otpSent ? (
            <>
              <label>Mobile Number</label>
              <input
                className="input_text"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={mobile}
                placeholder="Enter 10 digit mobile number"
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                maxLength={10}
                disabled={loading}
              />
              <CustomButton
                onClick={handleSendOtp}
                width="100%"
                className="login_btn"
                title={loading ? "Sending..." : "Send OTP"}
                disabled={loading}
              />
            </>
          ) : (
            <>
              <label>Enter OTP</label>
              <div className="otp_container">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    className="otp_box"
                    id={`otp-${i}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={(e) => handlePaste(e, i)}
                    autoComplete="one-time-code"
                    disabled={loading}
                  />
                ))}
              </div>

              <CustomButton
                onClick={handleVerify}
                width="100%"
                className="login_btn"
                title={loading ? "Verifying..." : "Verify OTP"}
                disabled={loading}
              />

              <div style={{ marginTop: 10, textAlign: "center" }}>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    color: "rgb(251 121 116)",
                    cursor: loading ? "not-allowed" : "pointer",
                    textDecoration: "underline",
                    fontSize: "14px",
                  }}
                >
                  {loading ? "Resending..." : "Resend OTP"}
                </button>
              </div>
            </>
          )}

          <div className="remember_me" style={{ marginTop: 18 }}>
            <label>
              <input type="checkbox" /> Remember me
            </label>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
